// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Overlay plumbing.
 *
 * THE MODAL DISMISSAL CONTRACT. A user who opens an overlay must always be able to get out of it,
 * and there is no such thing as an overlay with one way out. Every overlay here ships all four,
 * and they are not alternatives:
 *
 *   1. a close control inside the overlay, at least 44x44 CSS px of hit area
 *   2. a tap anywhere outside the panel, wired on `pointerdown` — which covers mouse, touch and
 *      pen in one handler and fires where a synthetic click would not
 *   3. Escape, from anywhere on the page
 *   4. a backdrop that is actually reachable: position fixed, inset 0, below the panel and above
 *      the page, and never pointer-events: none
 *
 * Dismissal is NEVER gated on re-tapping the control that opened the overlay — on a phone the
 * overlay covers it.
 *
 * One event family per control. A close button wired to both `touchstart` and `click` fires twice
 * on a real tap: it closes and instantly reopens, which the user experiences as a dead button and
 * which a desktop `.click()` cannot reproduce.
 */

export interface Overlay {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

const openStack: Overlay[] = [];

function push(o: Overlay): void {
  if (!openStack.includes(o)) openStack.push(o);
}
function pop(o: Overlay): void {
  const i = openStack.indexOf(o);
  if (i >= 0) openStack.splice(i, 1);
}

document.addEventListener(
  'keydown',
  (e) => {
    if (e.key !== 'Escape') return;
    const top = openStack[openStack.length - 1];
    if (top) {
      e.preventDefault();
      top.close();
    }
  },
  true,
);

export interface ModalHost {
  show(title: string, body: Node): void;
  close(): void;
  readonly isOpen: boolean;
}

export function createModalHost(root: HTMLElement): ModalHost {
  const backdrop = root.querySelector<HTMLElement>('.modal-backdrop')!;
  const panel = root.querySelector<HTMLElement>('.modal')!;
  const titleEl = root.querySelector<HTMLElement>('#modal-title')!;
  const bodyEl = root.querySelector<HTMLElement>('#modal-body')!;
  const closeBtn = root.querySelector<HTMLButtonElement>('#modal-close')!;
  let lastFocus: HTMLElement | null = null;

  const overlay: Overlay = {
    get isOpen() {
      return !root.hasAttribute('hidden');
    },
    open() {
      root.removeAttribute('hidden');
      push(overlay);
    },
    close() {
      root.setAttribute('hidden', '');
      pop(overlay);
      bodyEl.replaceChildren();
      lastFocus?.focus();
      lastFocus = null;
    },
  };

  // Exits 2 and 4: the backdrop is a real hit area, and pointerdown catches touch.
  backdrop.addEventListener('pointerdown', () => overlay.close());
  // Belt and braces for a pointer landing on the host but outside the panel.
  root.addEventListener('pointerdown', (e) => {
    if (!panel.contains(e.target as Node)) overlay.close();
  });
  // Exit 1. `click` alone — never click plus touchstart.
  closeBtn.addEventListener('click', () => overlay.close());

  return {
    get isOpen() {
      return overlay.isOpen;
    },
    show(title, body) {
      lastFocus = document.activeElement as HTMLElement | null;
      titleEl.textContent = title;
      bodyEl.replaceChildren(body);
      overlay.open();
      closeBtn.focus();
    },
    close: () => overlay.close(),
  };
}

export function createDrawer(
  root: HTMLElement,
  toggle: HTMLButtonElement,
  closeBtn: HTMLButtonElement,
): Overlay {
  const overlay: Overlay = {
    get isOpen() {
      return !root.hasAttribute('hidden');
    },
    open() {
      root.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      push(overlay);
    },
    close() {
      root.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      pop(overlay);
    },
  };

  toggle.addEventListener('click', () => (overlay.isOpen ? overlay.close() : overlay.open()));
  // The drawer covers its own toggle on a phone, so the in-drawer control is not optional.
  closeBtn.addEventListener('click', () => overlay.close());
  document.addEventListener('pointerdown', (e) => {
    if (!overlay.isOpen) return;
    const t = e.target as Node;
    if (root.contains(t) || toggle.contains(t)) return;
    overlay.close();
  });

  return overlay;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function toast(message: string): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    el.className = 'toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.removeAttribute('hidden');
  if (toastTimer) clearTimeout(toastTimer);
  // setTimeout, never requestAnimationFrame: rAF never fires in a hidden tab, and a toast that
  // never clears would sit over the page forever.
  toastTimer = setTimeout(() => el?.setAttribute('hidden', ''), 3200);
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) node.append(c);
  return node;
}

/**
 * Render a copy string that may contain <strong>, <em> and <code>.
 *
 * The strings come from `src/copy.ts` — first-party, not user input — but they are still built as
 * elements rather than assigned to innerHTML, so that nothing in this codebase establishes the
 * habit of putting a string into the DOM as markup.
 */
export function renderCopy(html: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const re = /<(strong|em|code)>(.*?)<\/\1>/gs;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m.index > last) frag.append(html.slice(last, m.index));
    const node = document.createElement(m[1]!);
    node.textContent = m[2]!;
    frag.append(node);
    last = m.index + m[0]!.length;
  }
  if (last < html.length) frag.append(html.slice(last));
  return frag;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-GB');
}
